import { useState, useCallback, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  FileSymlink,
  List,
  ListOrdered,
  ListChecks,
  ListTodo,
  ListPlus,
  Quote,
  CodeSquare,
  Minus,
  Table,
  Link,
  ChevronDown,
  Unlink,
  SquareCheckBig,
  ChevronsUpDown,
  StickyNote,
  Blocks,
  Link2,
  Paperclip,
  Underline as UnderlineIcon,
  Highlighter,
  Palette,
  RemoveFormatting,
  Flag,
  Scissors,
  Sigma,
} from 'lucide-react';
import { uploadAttachment } from './AttachmentUploadExtension';
import { SECTION_TYPES } from './AutoSectionExtension';
import { ColorPicker, type ColorKind } from './ColorPicker';
import { activeColor, applyColor, clearFormatting } from './FormattingExtensions';
import { createEntityFromText, insertReference as insertReferenceNode, referenceNode } from './createFromText';
import { truncateTitle } from './formatColors';
import { useEditorRerender } from './SelectionBar';
import { cellIsTotal } from './TableMathExtension';
import { TOTAL_FUNCTIONS } from '../../lib/tableMath';

interface EditorToolbarProps {
  editor: Editor | null;
  meetingInstanceId?: string;
  /** Owning scratchpad. Shows the attach-file control when present. */
  scratchpadId?: string;
  onTaskCreated?: () => void;
  onNoteCreated?: () => void;
  /**
   * Excerpt sources (#277, #239): with text selected, Create task/note makes
   * an excerpt-backed item - the same as the selection bar - instead of
   * replacing the text with a link.
   */
  onCreateExcerptItem?: (kind: 'task' | 'note', title: string, range: { from: number; to: number }) => Promise<void>;
  /** Flags (#316): opens the Flag menu for the line at the cursor. Absent where a document cannot carry flags. */
  onOpenFlagMenu?: (anchor: DOMRect) => void;
  /** Excerpts (#340): pick destinations for the selected range, like Ctrl+Shift+E. Absent where the editor is not an excerpt source. */
  onExcerpt?: (range: { from: number; to: number }) => void;
  /** Named lists (#337): open the list picker for the line or selection. Absent where lists are off. */
  onAddToList?: () => void;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, isActive, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      // Keep focus (and the cursor's pending formats) in the editor (#295).
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={isActive ?? false}
      className={`
        p-1 rounded-md transition-colors duration-100
        disabled:opacity-30 disabled:cursor-not-allowed
        ${isActive
          ? 'bg-accent/20 text-accent-hover'
          : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
        }
      `}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-4 bg-border mx-0.5" />;
}

/**
 * Attach a file from the toolbar.
 *
 * Drop and paste already work, but neither really exists on a phone: there is
 * no drag-and-drop, and pasting a file from the iOS Files app into a web
 * editor is awkward at best. This button is the only mobile path to attaching
 * anything, which is why it exists alongside them.
 */
function AttachFileButton({ editor, scratchpadId }: { editor: Editor; scratchpadId: string }) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const pos = editor.state.selection.from;
    Array.from(files).forEach((f) => {
      uploadAttachment(editor.view, f, pos, scratchpadId);
    });
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          handleFiles(e.target.files);
          // Clear so picking the SAME file twice in a row still fires onChange.
          e.target.value = '';
        }}
      />
      <ToolbarButton onClick={() => inputRef.current?.click()} title="Attach file">
        <Paperclip size={16} />
      </ToolbarButton>
    </>
  );
}

export function EditorToolbar({ editor, meetingInstanceId, scratchpadId, onTaskCreated, onNoteCreated, onCreateExcerptItem, onOpenFlagMenu, onExcerpt, onAddToList }: EditorToolbarProps) {
  const [headingOpen, setHeadingOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  // Replacing the selection is the default: leaving the original line behind
  // means a pad accumulates handled items with nothing to distinguish them
  // from live ones. Unticking keeps the text and appends the chip instead.
  const [replaceSelection, setReplaceSelection] = useState(true);
  // Captured when the popover opens, because focusing the input collapses the
  // editor selection - by commit time it is gone.
  const capturedRange = useRef<{ from: number; to: number } | undefined>(undefined);

  const [taskOpen, setTaskOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskCreating, setTaskCreating] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCreating, setNoteCreating] = useState(false);
  const [autoSectionOpen, setAutoSectionOpen] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLDivElement>(null);
  const autoSectionRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const taskRef = useRef<HTMLDivElement>(null);
  const flagRef = useRef<HTMLDivElement>(null);
  const taskInputRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const noteInputRef = useRef<HTMLInputElement>(null);

  // useEditor (TipTap v3) does not re-render on transactions, so without this
  // every pressed state below is whatever it was at the parent's last render.
  // Toggling a format with no selection only changes stored marks - no doc
  // change, no parent render - so the button looked stuck (#295).
  useEditorRerender(editor);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (headingRef.current && !headingRef.current.contains(e.target as Node)) {
        setHeadingOpen(false);
      }
      if (linkRef.current && !linkRef.current.contains(e.target as Node)) {
        setLinkOpen(false);
        setLinkUrl('');
      }
      if (taskRef.current && !taskRef.current.contains(e.target as Node)) {
        setTaskOpen(false);
        setTaskTitle('');
      }
      if (noteRef.current && !noteRef.current.contains(e.target as Node)) {
        setNoteOpen(false);
        setNoteTitle('');
      }
      if (autoSectionRef.current && !autoSectionRef.current.contains(e.target as Node)) {
        setAutoSectionOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Focus link input when dropdown opens
  useEffect(() => {
    if (linkOpen && linkInputRef.current) {
      const currentUrl = editor?.getAttributes('link').href || '';
      setLinkUrl(currentUrl);
      // Small delay to let the dropdown render
      requestAnimationFrame(() => linkInputRef.current?.focus());
    }
  }, [linkOpen, editor]);

  const setHeading = useCallback(
    (level: 1 | 2 | 3) => {
      editor?.chain().focus().toggleHeading({ level }).run();
      setHeadingOpen(false);
    },
    [editor],
  );

  const applyLink = useCallback(() => {
    if (!editor) return;
    const trimmed = linkUrl.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const url = /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkOpen(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
    setLinkUrl('');
  }, [editor]);

  const insertTable = useCallback(() => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  // Pre-fill task title from selected text when popover opens
  useEffect(() => {
    if (taskOpen && taskInputRef.current) {
      const selection = editor?.state?.selection;
      capturedRange.current = undefined;
      if (selection && !selection.empty) {
        const selectedText = editor.state.doc.textBetween(selection.from, selection.to, ' ');
        if (selectedText.trim()) {
          setTaskTitle(truncateTitle(selectedText));
          capturedRange.current = { from: selection.from, to: selection.to };
        }
      }
      requestAnimationFrame(() => taskInputRef.current?.focus());
    }
  }, [taskOpen, editor]);

  /**
   * Put the reference into the document.
   *
   * When replacing, the captured range is deleted first. The range is captured
   * at popover-open time because focusing the title input collapses the
   * editor's own selection, so by now editor.state.selection is empty.
   */
  const insertReference = useCallback((node: { type: string; attrs: Record<string, unknown> }) => {
    if (!editor) return;
    insertReferenceNode(editor, node, capturedRange.current, replaceSelection);
    capturedRange.current = undefined;
  }, [editor, replaceSelection]);

  const createTask = useCallback(async () => {
    if (!editor || !taskTitle.trim()) return;
    setTaskCreating(true);
    try {
      if (onCreateExcerptItem && capturedRange.current) {
        await onCreateExcerptItem('task', taskTitle.trim(), capturedRange.current);
        capturedRange.current = undefined;
      } else {
        const created = await createEntityFromText('task', taskTitle.trim(), { meetingInstanceId, scratchpadId });
        insertReference(referenceNode('task', created));
      }

      setTaskOpen(false);
      setTaskTitle('');
      onTaskCreated?.();
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setTaskCreating(false);
    }
  }, [editor, taskTitle, meetingInstanceId, scratchpadId, insertReference, onTaskCreated, onCreateExcerptItem]);

  // Pre-fill note title from selected text when popover opens
  useEffect(() => {
    if (noteOpen && noteInputRef.current) {
      const selection = editor?.state?.selection;
      capturedRange.current = undefined;
      if (selection && !selection.empty) {
        const selectedText = editor.state.doc.textBetween(selection.from, selection.to, ' ');
        if (selectedText.trim()) {
          setNoteTitle(truncateTitle(selectedText));
          capturedRange.current = { from: selection.from, to: selection.to };
        }
      }
      requestAnimationFrame(() => noteInputRef.current?.focus());
    }
  }, [noteOpen, editor]);

  const createNote = useCallback(async () => {
    if (!editor || !noteTitle.trim()) return;
    setNoteCreating(true);
    try {
      if (onCreateExcerptItem && capturedRange.current) {
        await onCreateExcerptItem('note', noteTitle.trim(), capturedRange.current);
        capturedRange.current = undefined;
      } else {
        const created = await createEntityFromText('note', noteTitle.trim(), { meetingInstanceId, scratchpadId });
        insertReference(referenceNode('note', created));
      }

      setNoteOpen(false);
      setNoteTitle('');
      onNoteCreated?.();
    } catch (err) {
      console.error('Failed to create note:', err);
    } finally {
      setNoteCreating(false);
    }
  }, [editor, noteTitle, meetingInstanceId, scratchpadId, insertReference, onNoteCreated, onCreateExcerptItem]);

  if (!editor) return null;

  // Determine active heading for the dropdown label
  const activeHeadingLevel = [1, 2, 3].find((l) =>
    editor.isActive('heading', { level: l }),
  ) as 1 | 2 | 3 | undefined;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border bg-bg-secondary overflow-x-auto sm:flex-wrap sm:overflow-x-visible mobile-toolbar-scroll">
      {/* Bold / Italic / Strikethrough / Code */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <Strikethrough size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        title="Inline code"
      >
        <Code size={16} />
      </ToolbarButton>

      <ColorButton editor={editor} kind="highlight" />
      <ColorButton editor={editor} kind="text" />

      <ToolbarButton
        onClick={() => clearFormatting(editor)}
        title="Clear formatting"
      >
        <RemoveFormatting size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Heading dropdown */}
      <div ref={headingRef} className="relative">
        <button
          type="button"
          onClick={() => setHeadingOpen((p) => !p)}
          title="Heading"
          className={`
            flex items-center gap-0.5 px-2 py-1 rounded-md text-sm transition-colors duration-100
            ${activeHeadingLevel
              ? 'bg-accent/20 text-accent-hover'
              : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
            }
          `}
        >
          <span className="font-medium text-xs">
            {activeHeadingLevel ? `H${activeHeadingLevel}` : 'H'}
          </span>
          <ChevronDown size={12} />
        </button>

        {headingOpen && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
            <button
              type="button"
              onClick={() => setHeading(1)}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors
                ${editor.isActive('heading', { level: 1 })
                  ? 'text-accent-hover bg-accent/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
            >
              <Heading1 size={16} /> Heading 1
            </button>
            <button
              type="button"
              onClick={() => setHeading(2)}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors
                ${editor.isActive('heading', { level: 2 })
                  ? 'text-accent-hover bg-accent/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
            >
              <Heading2 size={16} /> Heading 2
            </button>
            <button
              type="button"
              onClick={() => setHeading(3)}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors
                ${editor.isActive('heading', { level: 3 })
                  ? 'text-accent-hover bg-accent/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
            >
              <Heading3 size={16} /> Heading 3
            </button>
          </div>
        )}
      </div>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet list"
      >
        <List size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered list"
      >
        <ListOrdered size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        isActive={editor.isActive('taskList')}
        title="Task list"
      >
        <ListChecks size={16} />
      </ToolbarButton>

      {/* Named list (#337): a list lines can be filed into */}
      <ToolbarButton
        onClick={() => editor.chain().focus().insertNamedList().run()}
        title="Named list"
      >
        <ListTodo size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Block elements */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <Quote size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code block"
      >
        <CodeSquare size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal rule"
      >
        <Minus size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Table */}
      <ToolbarButton
        onClick={insertTable}
        title="Insert table"
      >
        <Table size={16} />
      </ToolbarButton>
      {editor.isActive('table') && <TableMenu editor={editor} />}

      {/* Link */}
      <div ref={linkRef} className="relative">
        <ToolbarButton
          onClick={() => setLinkOpen((p) => !p)}
          isActive={editor.isActive('link')}
          title="Link"
        >
          <Link size={16} />
        </ToolbarButton>

        {linkOpen && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg p-2 min-w-[280px]">
            <div className="flex items-center gap-1.5">
              <input
                ref={linkInputRef}
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                  if (e.key === 'Escape') {
                    setLinkOpen(false);
                    setLinkUrl('');
                    editor.commands.focus();
                  }
                }}
                placeholder="https://example.com"
                className="flex-1 px-2.5 py-1.5 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
              />
              <button
                type="button"
                onClick={applyLink}
                className="px-2.5 py-1.5 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
              >
                Apply
              </button>
              {editor.isActive('link') && (
                <button
                  type="button"
                  onClick={removeLink}
                  title="Remove link"
                  className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-bg-hover transition-colors"
                >
                  <Unlink size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Collapsible Section */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setDetails().run()}
        title="Collapsible section"
      >
        <ChevronsUpDown size={16} />
      </ToolbarButton>

      {/* File Link */}
      <FileLinkButton editor={editor} />
      {scratchpadId && <AttachFileButton editor={editor} scratchpadId={scratchpadId} />}

      {/* Auto Section insert */}
      <div ref={autoSectionRef} className="relative">
        <ToolbarButton
          onClick={() => setAutoSectionOpen((p) => !p)}
          title="Insert auto-section"
        >
          <Blocks size={16} />
        </ToolbarButton>

        {autoSectionOpen && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg py-1 min-w-[220px]">
            {SECTION_TYPES.map(section => (
              <button
                key={section.value}
                type="button"
                onClick={() => {
                  editor.chain().focus().insertContent({
                    type: 'autoSection',
                    attrs: { sectionType: section.value },
                  }).run();
                  setAutoSectionOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-sm flex flex-col gap-0.5 transition-colors text-text-secondary hover:text-text-primary hover:bg-bg-hover"
              >
                <span className="font-medium">{section.label}</span>
                <span className="text-[10px] text-text-muted">{section.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <ToolbarDivider />

      {/* Flag the line at the cursor (#316) */}
      {onOpenFlagMenu && (
        <div ref={flagRef} className="relative">
          <ToolbarButton onClick={() => onOpenFlagMenu(flagRef.current!.getBoundingClientRect())} title="Flag (Alt+1 to Alt+9)">
            <Flag size={16} />
          </ToolbarButton>
        </div>
      )}

      {/* Create Task */}
      <div ref={taskRef} className="relative">
        <ToolbarButton
          onClick={() => setTaskOpen((p) => !p)}
          title="Create task"
        >
          <SquareCheckBig size={16} />
        </ToolbarButton>

        {taskOpen && (
          <div className="absolute top-full right-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg p-2 min-w-[300px]">
            <p className="text-xs text-text-muted mb-1.5 px-0.5">Create a new task</p>
            <div className="flex items-center gap-1.5">
              <input
                ref={taskInputRef}
                type="text"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    createTask();
                  }
                  if (e.key === 'Escape') {
                    setTaskOpen(false);
                    setTaskTitle('');
                    editor.commands.focus();
                  }
                }}
                placeholder="Task title..."
                disabled={taskCreating}
                className="flex-1 px-2.5 py-1.5 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={createTask}
                disabled={taskCreating || !taskTitle.trim()}
                className="px-2.5 py-1.5 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {taskCreating ? '...' : 'Create'}
              </button>
            </div>
                      {!onCreateExcerptItem && (
                      <label className="flex items-center gap-1.5 mt-2 text-xs text-text-muted cursor-pointer select-none">
              <input
                type="checkbox"
                checked={replaceSelection}
                onChange={(e) => setReplaceSelection(e.target.checked)}
                disabled={taskCreating}
                className="accent-accent"
              />
              Replace selected text with a link
            </label>
                      )}
</div>
        )}
      </div>

      {/* Create Note */}
      <div ref={noteRef} className="relative">
        <ToolbarButton
          onClick={() => setNoteOpen((p) => !p)}
          title="Create note"
        >
          <StickyNote size={16} />
        </ToolbarButton>

        {noteOpen && (
          <div className="absolute top-full right-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg p-2 min-w-[300px]">
            <p className="text-xs text-text-muted mb-1.5 px-0.5">Create a new note</p>
            <div className="flex items-center gap-1.5">
              <input
                ref={noteInputRef}
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    createNote();
                  }
                  if (e.key === 'Escape') {
                    setNoteOpen(false);
                    setNoteTitle('');
                    editor.commands.focus();
                  }
                }}
                placeholder="Note title..."
                disabled={noteCreating}
                className="flex-1 px-2.5 py-1.5 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={createNote}
                disabled={noteCreating || !noteTitle.trim()}
                className="px-2.5 py-1.5 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {noteCreating ? '...' : 'Create'}
              </button>
            </div>
                      {!onCreateExcerptItem && (
                      <label className="flex items-center gap-1.5 mt-2 text-xs text-text-muted cursor-pointer select-none">
              <input
                type="checkbox"
                checked={replaceSelection}
                onChange={(e) => setReplaceSelection(e.target.checked)}
                disabled={noteCreating}
                className="accent-accent"
              />
              Replace selected text with a link
            </label>
                      )}
</div>
        )}
      </div>

      {/* Excerpt the selection (#340) */}
      {onExcerpt && (
        <ToolbarButton
          onClick={() => {
            const { from, to, empty } = editor.state.selection;
            if (!empty) onExcerpt({ from, to });
          }}
          disabled={editor.state.selection.empty}
          title="Excerpt selection (Ctrl+Shift+E)"
        >
          <Scissors size={16} />
        </ToolbarButton>
      )}

      {/* File the line or selection into a named list (#337) */}
      {onAddToList && (
        <ToolbarButton onClick={onAddToList} title="Add to list (Alt+L)">
          <ListPlus size={16} />
        </ToolbarButton>
      )}

      {/* Link existing entity */}
      <ToolbarButton
        onClick={() => editor.chain().focus().insertContent('[[').run()}
        title="Link existing note or task"
      >
        <Link2 size={16} />
      </ToolbarButton>
    </div>
  );
}

/**
 * Table menu (#338): rows, columns and totals for the table at the cursor.
 * A total is an ordinary `=FN(ABOVE)` / `=FN(LEFT)` formula, so picking a
 * function here just rewrites that formula.
 */
function TableMenu({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const total = cellIsTotal(editor.state);
  const run = (fn: () => void) => () => { fn(); setOpen(false); };
  const itemClass = 'w-full text-left px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors';

  return (
    <div ref={ref} className="relative">
      <ToolbarButton onClick={() => setOpen((o) => !o)} isActive={open} title="Table: rows, columns and totals">
        <Sigma size={16} />
      </ToolbarButton>
      {open && (
        <div role="menu" className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg py-1 min-w-[180px]">
          <button type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} className={itemClass}
            onClick={run(() => editor.chain().focus().addRowAfter().run())}>
            Add row below
          </button>
          <button type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} className={itemClass}
            onClick={run(() => editor.chain().focus().addColumnAfter().run())}>
            Add column right
          </button>
          <div className="my-1 border-t border-border" />
          <button type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} className={itemClass}
            onClick={run(() => editor.chain().focus().addTotalRow().run())}>
            Add total row
          </button>
          <button type="button" role="menuitem" onMouseDown={(e) => e.preventDefault()} className={itemClass}
            onClick={run(() => editor.chain().focus().addTotalColumn().run())}>
            Add total column
          </button>
          {total && (
            <>
              <div className="my-1 border-t border-border" />
              <div className="px-3 py-1 text-xs text-text-muted">Total of this cell</div>
              {TOTAL_FUNCTIONS.map(({ fn, label }) => (
                <button
                  key={fn}
                  type="button"
                  role="menuitemradio"
                  aria-checked={total.fn === fn}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={run(() => editor.chain().focus().setTotalFunction(fn).run())}
                  className={`${itemClass} ${total.fn === fn ? 'text-accent-hover bg-accent/10' : ''}`}
                >
                  {label}
                </button>
              ))}
            </>
          )}
          <div className="px-3 pt-1.5 pb-1 text-xs text-text-muted border-t border-border mt-1">
            Or type a formula: =SUM(B2:B5)
          </div>
        </div>
      )}
    </div>
  );
}

/** Highlight / text colour dropdown. */
function ColorButton({ editor, kind }: { editor: Editor; kind: ColorKind }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const active = activeColor(editor, kind);

  return (
    <div ref={ref} className="relative">
      <ToolbarButton
        onClick={() => setOpen((p) => !p)}
        isActive={kind === 'highlight' ? editor.isActive('highlight') : !!active}
        title={kind === 'highlight' ? 'Highlight' : 'Text colour'}
      >
        {kind === 'highlight' ? <Highlighter size={16} /> : <Palette size={16} />}
      </ToolbarButton>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg">
          <ColorPicker
            kind={kind}
            active={active}
            onPick={(c) => { applyColor(editor, kind, c); setOpen(false); }}
          />
        </div>
      )}
    </div>
  );
}

function FileLinkButton({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; filename: string }[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!query.trim() || !open) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/attachments/search?q=${encodeURIComponent(query.trim())}&limit=8`, {
          headers: { Authorization: `Bearer ${await (await import('../../lib/firebase')).getIdToken()}` },
        });
        if (res.ok) setResults(await res.json());
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(timer);
  }, [query, open]);

  function insertFileLink(file: { id: string; filename: string }) {
    editor.chain().focus().insertContent({
      type: 'entityLink',
      attrs: {
        entityType: 'attachment',
        entityId: file.id,
        label: file.filename,
      },
    }).run();
    setOpen(false);
    setQuery('');
    setResults([]);
  }

  return (
    <div ref={ref} className="relative">
      <ToolbarButton onClick={() => setOpen(!open)} title="Link to file">
        <FileSymlink size={16} />
      </ToolbarButton>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg w-64 overflow-hidden">
          <div className="p-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search files..."
              className="w-full px-2 py-1.5 bg-bg-input border border-border rounded text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
            />
          </div>
          {results.length > 0 && (
            <div className="max-h-48 overflow-y-auto border-t border-border">
              {results.map(f => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => insertFileLink(f)}
                  className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors truncate"
                >
                  {f.filename}
                </button>
              ))}
            </div>
          )}
          {query.trim() && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-text-muted border-t border-border">No files found</div>
          )}
        </div>
      )}
    </div>
  );
}
